<template>
    <div class="app-header-step-indication d-flex align-items-center align-items-md-stretch" @click="dropdownVisible = !dropdownVisible" v-click-outside="hideDropdown">
        <i class="app-header-step-indication__badge d-flex flex-column justify-content-between d-md-none">
            <span class="app-header-step-indication__badge-active-idx">01</span>
            <span class="app-header-step-indication__badge-last-idx">04</span>
        </i>
        <span class="app-header-step-indication__active-step d-md-none">{{ activeStepName }}</span>
        <div class="app-header-step-indication__steps d-md-flex" v-show="dropdownVisible">
            <div class="app-header-step-indication__steps-inner d-md-flex align-items-center">
                <template v-for="(step, idx) in steps">
                    <template v-if="step.skip !== true">
                        <i v-if="idx > 0"
                            :key="idx + '-divider'"
                            class="app-header-step-indication__step-divider d-none d-md-block"
                            :class="{
                                'app-header-step-indication__step-divider--finished': idx < activeStepIdx,
                                'app-header-step-indication__step-divider--active': idx === activeStepIdx
                            }"
                        />
                        <router-link tag="div" :to="step.route" class="app-header-step-indication__step d-flex align-items-center"
                            :key="idx"
                            :class="{
                                'app-header-step-indication__step--finished': idx < activeStepIdx,
                                'app-header-step-indication__step--active': idx === activeStepIdx
                            }"
                        >
                            {{ step.name }}
                        </router-link>
                    </template>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import ClickOutside from 'vue-click-outside'

const [, languageCode, countryCode] = process.env.VUE_APP_I18N_LOCALE.match(/([a-z]{2})(?:-([a-z]{2}))?$/i)
const isGermany = countryCode === 'DE' || (languageCode === 'de' && !countryCode)

export default {
    name: 'AppHeaderStepIndication',
    directives: {
        ClickOutside
    },
    data: () => ({
        dropdownVisible: false
    }),
    computed: {
        ...mapState({
            productPathSegment: 'productPathSegment'
        }),
        product: (vm) => vm.$store.getters.productMapped,
        //higher step index than the last means all steps have been solved
        activeStepIdx: (vm) => vm.$store.state.currentStep === vm.steps.length ? vm.steps.length - 1 : vm.$store.state.currentStep,
        steps () {
            return [
                {
                    name: this.$t('product.headline'),
                    route: { name: 'steps', hash: '', params: { productPathSegment: this.productPathSegment } }
                },
                {
                    name: this.$t('priceCalculation.headline'),
                    route: { name: 'steps', hash: '#price-calculation', params: { productPathSegment: this.productPathSegment } }
                },
                {
                    name: this.$t('productOptions.headline'),
                    route: { name: 'steps', hash: '#product-options', params: { productPathSegment: this.productPathSegment } },
                    skip: !this.product.options.length || isGermany
                },
                {
                    name: this.$t('personalData.headline'),
                    route: { name: 'steps', hash: '#personal-data', params: { productPathSegment: this.productPathSegment } }
                }
            ]
        },
        activeStepName: (vm) => vm.steps[vm.activeStepIdx].name
    },
    methods: {
        hideDropdown () {
            this.dropdownVisible = false
        }
    }
}
</script>

<style scoped>
    .app-header-step-indication {
        position: relative;
        width: 100%;
        height: 100%;
        color: #cbc9c8;
        font-weight: 700;
        padding: 8px 25px 8px 10px;
    }
    .app-header-step-indication::before {
        content: '';
        position: absolute;
        top: 50%;
        right: 10px;
        width: 18px;
        height: 18px;
        background-image: url("../assets/img/arrow.svg");
        background-repeat: no-repeat;
        transform: translateY(-50%);
        pointer-events: none;
    }
    .app-header-step-indication__active-step {
        width: 100%;
    }
    .app-header-step-indication__active-step,
    .app-header-step-indication__badge-active-idx,
    .app-header-step-indication__step--active {
        color: #545454;
    }
    .app-header-step-indication__badge {
        position: relative;
        height: 32px;
        margin-right: 10px;
        font-style: normal;
        line-height: 1;
        pointer-events: none;
    }
    .app-header-step-indication__badge::before {
        content: '';
        position: absolute;
        left: 0;
        top: 50%;
        right: 0;
        height: 2px;
        background-color: currentColor;
        transform: translateY(-50%);
    }
    .app-header-step-indication__steps {
        position: absolute;
        left: 0;
        top: 100%;
        right: 0;
        margin: 0 -10px;
        padding: 0 10px 10px;
        counter-reset: step-indication-steps;
        overflow: hidden;
    }
    .app-header-step-indication__steps-inner {
        background-color: #fff;
        box-shadow: 0 1px 10px 0 rgba(0, 0, 0, .2);
    }
    .app-header-step-indication__step {
        position: relative;
        min-height: 42px;
        padding: 8px 25px 8px 36px;
        border-top: 1px solid #e6e6e6;
        cursor: default;
        /* avoid clicks on inactive steps */
        pointer-events: none;
    }
    .app-header-step-indication__step::before,
    .app-header-step-indication__step::after {
        content: '';
        position: absolute;
        left: 10px;
        top: 50%;
        width: 16px;
        height: 16px;
        transform: translateY(-50%);
        pointer-events: none;
    }
    .app-header-step-indication__step::after {
        content: '0' counter(step-indication-steps);
        counter-increment: step-indication-steps;
        text-align: center;
    }
    .app-header-step-indication__step--active,
    .app-header-step-indication__step--finished {
        color: #f96915;
        pointer-events: auto;
    }
    .app-header-step-indication__step--finished::before {
        border-radius: 50%;
        background-color: currentColor;
    }
    .app-header-step-indication__step--finished::after {
        content: '';
        background: transparent url("../assets/img/tick.svg") no-repeat center center;
        background-size: 50% 50%;
    }

    @media ( min-width: 768px ) {
        .app-header-step-indication {
            padding: 0;
            color: #969492;
        }
        .app-header-step-indication::before {
            display: none;
        }
        .app-header-step-indication__steps {
            position: static;
            width: 100%;
            margin: 0;
            padding: 0;
        }
        .app-header-step-indication__steps-inner {
            width: 100%;
            box-shadow: none;
        }
        .app-header-step-indication__step {
            /* IE 11 fix */
            flex: 0 1 auto;
            height: 100%;
            min-height: 0;
            padding: 8px 20px 8px 60px;
            border-top: 0;
        }
        .app-header-step-indication__step::before,
        .app-header-step-indication__step::after {
            left: 20px;
            width: 30px;
            height: 30px;
        }
        .app-header-step-indication__step::before {
            border-radius: 50%;
            background-color: #e6e6e6;
        }
        .app-header-step-indication__step::after {
            font-weight: 400;
            line-height: 30px;
            color: #969492;
        }
        .app-header-step-indication__step--active::before,
        .app-header-step-indication__step--finished::before {
            background-color: currentColor;
        }
        .app-header-step-indication__step--active::after {
            font-weight: 700;
            color: #fff;
        }
        .app-header-step-indication__step:first-child {
            padding-left: 55px;
        }
        .app-header-step-indication__step:first-child::before,
        .app-header-step-indication__step:first-child::after {
            left: 15px;
        }
        .app-header-step-indication__step-divider {
            flex: 1 0 10px;
            max-width: 80px;
            height: 2px;
            background-color: #e6e6e6;
        }
        .app-header-step-indication__step-divider--active,
        .app-header-step-indication__step-divider--finished {
            background-color: #f96915;
        }
    }
</style>
